import React, { useEffect, useMemo, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Check,
  ChevronDownIcon,
  Clock,
  Plus,
  QrCode,
  Save,
  Upload,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import RichTextInput from "@/components/common/RichTextInput";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getHotelSaveErrorKey } from "@/components/pages/hotels/utils/fileUploadOutcome";
import { useHotelUpdate } from "@/components/pages/hotels/hooks/useHotels";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { canSaveHotelAddress } from "@/components/pages/general-info/utils/hotelAddressAccess";
import {
  GeneralInfoFormData,
  generalInfoSchema,
} from "@/components/pages/general-info/utils/generalInfoSchema";
import {
  TIME_OPTIONS,
  filterTimezoneOptions,
  getTimezoneOptions,
  normalizeTimezone,
} from "@/components/pages/general-info/utils/generalInfoOptions";
import { HotelMapPreview } from "@/components/pages/general-info/components/HotelMapPreview";
import { cn } from "@/lib/utils";
import { Hotel, WIFI_SECURITY_OPTIONS, WifiSecurity } from "@/types/hotel";
import { Role } from "@/types/user";
import { UserProfile } from "@/types/user-profile";
import { countries } from "@/utils/countries";
import { PolicyRuleDialog } from "@/components/pages/general-info/components/PolicyRuleDialog";
import { appendPolicyRule } from "@/components/pages/general-info/utils/policyRule";
import { GeneralInfoLogoUploader } from "@/components/pages/general-info/components/GeneralInfoLogoUploader";
import { RoomQrDialog } from "@/components/pages/reservations/rooms/components/RoomQrDialog";

interface GeneralInfoFormProps {
  hotel: Hotel;
  managers: UserProfile[];
}

const FieldError = ({
  message,
}: {
  message?: string;
}): React.JSX.Element | null =>
  message ? <p className="text-destructive mt-1.5 text-sm">{message}</p> : null;

interface TimezoneComboboxProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  searchPlaceholder: string;
  emptyLabel: string;
  invalid?: boolean;
}

/** Searchable select over IANA zones, each labelled with its current UTC offset (DV-140). */
const TimezoneCombobox = ({
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  invalid,
}: TimezoneComboboxProps): React.JSX.Element => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const options = useMemo(() => getTimezoneOptions(value), [value]);
  const filteredOptions = useMemo(
    () => filterTimezoneOptions(options, query),
    [options, query],
  );
  const selectedLabel =
    options.find((option) => option.value === value)?.label ?? value;

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setQuery("");
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-invalid={invalid}
          className={cn(
            "flex h-12 w-full items-center justify-between gap-2 rounded-md border bg-white px-4 py-2 text-sm font-normal whitespace-nowrap shadow-xs outline-none",
            "border-base-border text-black/96",
            "focus-visible:border-custom-outline focus-visible:ring-custom-outline/50 focus-visible:ring-[3px]",
            "aria-invalid:border-custom-destructive-dark",
          )}
        >
          <span
            className={cn(
              "truncate",
              !selectedLabel && "text-muted-foreground",
            )}
          >
            {selectedLabel || placeholder}
          </span>
          <ChevronDownIcon className="size-4 shrink-0 text-black" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-(--radix-popover-trigger-width) min-w-72 p-2"
      >
        <Input
          autoFocus
          value={query}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          onChange={(event) => setQuery(event.target.value)}
        />
        <div role="listbox" className="mt-2 max-h-72 overflow-y-auto">
          {filteredOptions.length === 0 ? (
            <p className="text-muted-foreground px-2 py-1.5 text-sm">
              {emptyLabel}
            </p>
          ) : (
            filteredOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={option.value === value}
                className="hover:bg-accent focus-visible:bg-accent flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none"
                onClick={() => {
                  onChange(option.value);
                  handleOpenChange(false);
                }}
              >
                <span className="truncate">{option.label}</span>
                {option.value === value && (
                  <Check className="size-4 shrink-0" />
                )}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const getCountryCode = (country?: string): string => {
  const normalizedCountry = country?.trim();

  if (!normalizedCountry) {
    return "";
  }

  if (/^[A-Za-z]{2}$/.test(normalizedCountry)) {
    return normalizedCountry.toUpperCase();
  }

  return (
    countries.find(
      ({ name }) => name.toLowerCase() === normalizedCountry.toLowerCase(),
    )?.code ?? ""
  );
};

const formatManagerLabel = (manager: UserProfile): string => {
  const fullName = [manager.firstName, manager.lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");

  return fullName && manager.email
    ? `${fullName} - ${manager.email}`
    : fullName || manager.email || manager.id;
};

const getDefaultValues = (hotel: Hotel): GeneralInfoFormData => ({
  name: hotel.name,
  logoId: hotel.logoId,
  address: {
    street: hotel.hotelAddress?.street ?? "",
    city: hotel.hotelAddress?.city ?? "",
    state: hotel.hotelAddress?.state ?? "",
    country: getCountryCode(hotel.hotelAddress?.country),
    postalCode: hotel.hotelAddress?.postalCode ?? "",
    googlePlaceId: hotel.hotelAddress?.googlePlaceId ?? "",
    googleMapsCid: hotel.hotelAddress?.googleMapsCid ?? "",
  },
  pms: hotel.pms,
  wifiNetwork: hotel.wifiNetwork ?? "",
  wifiPassword: hotel.wifiPassword ?? "",
  wifiSecurity: hotel.wifiSecurity ?? WifiSecurity.WPA,
  mapFileId: hotel.mapFileId ?? "",
  policyText: hotel.policyText ?? "",
  description: hotel.description ?? "",
  // Legacy "GMT±H:00" values not yet migrated show as their Etc/GMT∓H zone.
  timezone: normalizeTimezone(hotel.timezone ?? ""),
  website: hotel.website ?? "",
  checkInTime: hotel.checkInTime ?? "11:00",
  checkOutTime: hotel.checkOutTime ?? "14:00",
  socialMediaInstagram: hotel.socialMediaInstagram ?? "",
  socialMediaFacebook: hotel.socialMediaFacebook ?? "",
  socialMediaOther: hotel.socialMediaOther ?? "",
  managerIds:
    hotel.employees
      ?.filter(
        (employee) =>
          employee.role === Role.HOTEL_ADMIN ||
          employee.role === Role.TENANT_ADMIN,
      )
      .map((employee) => employee.id) ?? [],
  chatSettings: hotel.chatSettings
    ? {
        isAiEnabled: hotel.chatSettings.isAiEnabled,
        isTranslationEnabled: hotel.chatSettings.isTranslationEnabled,
        defaultLanguage: hotel.chatSettings.defaultLanguage,
      }
    : undefined,
});

export const GeneralInfoForm = ({
  hotel,
  managers,
}: GeneralInfoFormProps): React.JSX.Element => {
  const { t } = useTranslation();
  const { handleHotelSubmit, isLoading: isUpdating } = useHotelUpdate(hotel.id);
  const { userProfile } = useAuthenticatedUser();
  // Place ID and CID live in the address, which the backend saves only for Tenant Admin.
  const canEditMapsPlace = canSaveHotelAddress(userProfile);
  const [selectedLogoFile, setSelectedLogoFile] = useState<File>();
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string>();
  const [selectedMapFile, setSelectedMapFile] = useState<File>();
  const mapInputRef = useRef<HTMLInputElement>(null);
  const [isRuleDialogOpen, setIsRuleDialogOpen] = useState(false);
  const [policyText, setPolicyText] = useState<string | undefined>(
    hotel.policyText,
  );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<GeneralInfoFormData>({
    resolver: zodResolver(generalInfoSchema),
    defaultValues: getDefaultValues(hotel),
  });

  useEffect(() => {
    reset(getDefaultValues(hotel));
    setPolicyText(hotel.policyText);
    setSelectedLogoFile(undefined);
    setSelectedMapFile(undefined);
    setLogoPreviewUrl(undefined);
  }, [hotel, reset]);

  useEffect(
    () => () => {
      if (logoPreviewUrl) {
        URL.revokeObjectURL(logoPreviewUrl);
      }
    },
    [logoPreviewUrl],
  );

  const selectedCountry = watch("address.country");
  const selectedTimezone = watch("timezone");
  const selectedCheckInTime = watch("checkInTime");
  const selectedCheckOutTime = watch("checkOutTime");
  const selectedManagers = watch("managerIds") ?? [];
  const assignedManager = managers.find((manager) =>
    selectedManagers.includes(manager.id),
  );
  const countryName =
    countries.find(({ code }) => code === selectedCountry)?.name ??
    hotel.hotelAddress?.country ??
    "";
  const preferredLanguage = hotel.chatSettings?.defaultLanguage
    ? hotel.chatSettings.defaultLanguage.toUpperCase()
    : t("hotels.generalInfo.notConfigured");
  const displayCurrency = hotel.paymentSettings?.currency
    ? hotel.paymentSettings.currency.toUpperCase()
    : t("hotels.generalInfo.notConfigured");
  const hasPolicyChanges = (policyText ?? "") !== (hotel.policyText ?? "");
  const hasFileChanges = Boolean(selectedLogoFile || selectedMapFile);
  const isSaving = isSubmitting || isUpdating;
  const canSave = isDirty || hasPolicyChanges || hasFileChanges;

  const handleLogoChange = (file: File) => {
    if (logoPreviewUrl) {
      URL.revokeObjectURL(logoPreviewUrl);
    }

    setSelectedLogoFile(file);
    setLogoPreviewUrl(URL.createObjectURL(file));
  };

  const handleMapFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      setSelectedMapFile(file);
    }
  };

  const onSubmit = async (values: GeneralInfoFormData) => {
    try {
      // General Info does not own payment settings; keep currency managed by Billing Settings.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars
      const { paymentSettings: _, ...hotelValues } = values;
      // Empty social links must be sent as null, not "", so the backend clears
      // them instead of rejecting an empty string as an invalid URL.
      const normalizedValues = {
        ...hotelValues,
        socialMediaInstagram: hotelValues.socialMediaInstagram || null,
        socialMediaFacebook: hotelValues.socialMediaFacebook || null,
        socialMediaOther: hotelValues.socialMediaOther || null,
      };
      await handleHotelSubmit(
        { ...normalizedValues, policyText },
        selectedLogoFile,
        hotel.logoId,
        selectedMapFile,
        hotel.mapFileId,
      );
      reset({ ...hotelValues, policyText });
      setSelectedLogoFile(undefined);
      setSelectedMapFile(undefined);
      toast.success(t("hotels.form.hotelUpdatedSuccessfully"), {
        id: "general-info-save",
      });
    } catch (error) {
      toast.error(t(getHotelSaveErrorKey(error)), { id: "general-info-save" });
      console.error("Error updating hotel general information:", error);
    }
  };

  return (
    <main className="bg-background min-h-full p-4 sm:p-6 lg:p-10">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-8">
        <header className="flex flex-col items-center gap-4 text-center">
          <GeneralInfoLogoUploader
            fileId={hotel.logoId}
            previewUrl={logoPreviewUrl}
            alt={t("hotels.generalInfo.logoAlt", { name: hotel.name })}
            changeLabel={t("hotels.generalInfo.changeLogo")}
            onChange={handleLogoChange}
          />
          <div className="flex max-w-xl flex-col gap-1">
            <h1 className="text-foreground text-2xl font-semibold tracking-tight">
              {hotel.name}
            </h1>
            <p className="text-muted-foreground text-sm">
              {t("hotels.generalInfo.description")}
            </p>
          </div>
          {/* Task 0041, contract 2026-09-30 — hotel-wide/reception QR, also
              surfaced on the rooms page next to each room's own. */}
          <RoomQrDialog
            room={null}
            hotelId={hotel.id}
            trigger={
              <Button type="button" variant="outline" size="sm">
                <QrCode className="h-4 w-4" />
                {t("hotels.generalInfo.hotelWideQr", "Hotel QR (reception)")}
              </Button>
            }
          />
        </header>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-6"
          noValidate
        >
          <Card className="border-0 bg-[#F3F4F6] shadow-none">
            <CardContent className="grid gap-5 pt-2 md:grid-cols-2">
              <Input
                label={`${t("hotels.generalInfo.hotelBrandName")} *`}
                value={hotel.name}
                readOnly
              />

              <div className="hidden md:block" aria-hidden="true" />

              <div className="flex flex-col gap-1.5">
                <label className="text-foreground text-sm font-medium">
                  {t("hotels.generalInfo.checkInTime")} *
                </label>
                <Select
                  value={selectedCheckInTime}
                  onValueChange={(value) =>
                    setValue("checkInTime", value, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                >
                  <SelectTrigger className="w-full">
                    <Clock className="size-4" />
                    <SelectValue
                      placeholder={t("hotels.generalInfo.selectTime")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldError message={errors.checkInTime?.message} />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-foreground text-sm font-medium">
                  {t("hotels.generalInfo.checkOutTime")} *
                </label>
                <Select
                  value={selectedCheckOutTime}
                  onValueChange={(value) =>
                    setValue("checkOutTime", value, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                >
                  <SelectTrigger className="w-full">
                    <Clock className="size-4" />
                    <SelectValue
                      placeholder={t("hotels.generalInfo.selectTime")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldError message={errors.checkOutTime?.message} />
              </div>

              <Input
                label={`${t("hotels.generalInfo.preferredLanguage")} *`}
                value={preferredLanguage}
                readOnly
              />
              <Input
                label={`${t("hotels.generalInfo.displayCurrency")} *`}
                value={displayCurrency}
                readOnly
              />
              <div>
                <Input
                  type="url"
                  label={`${t("hotels.generalInfo.website")} *`}
                  placeholder={t("hotels.generalInfo.websitePlaceholder")}
                  aria-invalid={Boolean(errors.website)}
                  {...register("website")}
                />
                <FieldError message={errors.website?.message} />
              </div>
              <div className="md:col-span-2">
                <Textarea
                  label={t("hotels.generalInfo.hotelDescription")}
                  placeholder={t(
                    "hotels.generalInfo.hotelDescriptionPlaceholder",
                  )}
                  description={t("hotels.generalInfo.hotelDescriptionHint")}
                  maxLength={2000}
                  rows={4}
                  aria-invalid={Boolean(errors.description)}
                  {...register("description")}
                />
                <FieldError message={errors.description?.message} />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 bg-[#F3F4F6] shadow-none">
            <CardHeader>
              <CardTitle className="text-primary text-xl font-normal">
                {t("hotels.generalInfo.addressInformation")}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-5 md:grid-cols-2">
              <Input
                label={`${t("hotels.form.country")} *`}
                value={countryName}
                readOnly
              />

              <div className="flex flex-col gap-1.5">
                <label className="text-foreground text-sm font-medium">
                  {t("hotels.generalInfo.timezone")} *
                </label>
                <TimezoneCombobox
                  value={selectedTimezone}
                  onChange={(value) =>
                    setValue("timezone", value, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                  placeholder={t("hotels.generalInfo.selectTimezone")}
                  searchPlaceholder={t(
                    "hotels.generalInfo.searchTimezone",
                    "Search time zone",
                  )}
                  emptyLabel={t(
                    "hotels.generalInfo.noTimezoneFound",
                    "No time zone found",
                  )}
                  invalid={Boolean(errors.timezone)}
                />
                <FieldError message={errors.timezone?.message} />
              </div>

              <Input
                label={t("hotels.form.state")}
                value={hotel.hotelAddress?.state ?? ""}
                readOnly
              />

              <Input
                label={`${t("hotels.form.city")} *`}
                value={hotel.hotelAddress?.city ?? ""}
                readOnly
              />

              <Input
                label={`${t("hotels.form.street")} *`}
                value={hotel.hotelAddress?.street ?? ""}
                readOnly
              />

              <Input
                label={t("hotels.form.postalCode")}
                value={hotel.hotelAddress?.postalCode ?? ""}
                readOnly
              />

              <div>
                <Input
                  label={t("hotels.generalInfo.googlePlaceId")}
                  placeholder={
                    canEditMapsPlace
                      ? t("hotels.generalInfo.googlePlaceIdPlaceholder")
                      : undefined
                  }
                  description={t("hotels.generalInfo.googlePlaceIdHint")}
                  readOnly={!canEditMapsPlace}
                  aria-invalid={Boolean(errors.address?.googlePlaceId)}
                  {...register("address.googlePlaceId")}
                />
                <FieldError message={errors.address?.googlePlaceId?.message} />
              </div>

              <div>
                <Input
                  label={t("hotels.generalInfo.googleMapsCid")}
                  placeholder={
                    canEditMapsPlace
                      ? t("hotels.generalInfo.googleMapsCidPlaceholder")
                      : undefined
                  }
                  description={t("hotels.generalInfo.googleMapsCidHint")}
                  readOnly={!canEditMapsPlace}
                  inputMode="numeric"
                  aria-invalid={Boolean(errors.address?.googleMapsCid)}
                  {...register("address.googleMapsCid")}
                />
                <FieldError message={errors.address?.googleMapsCid?.message} />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 bg-[#F3F4F6] shadow-none">
            <CardHeader>
              <CardTitle className="text-primary text-xl font-normal">
                {t("hotels.generalInfo.wlanInformation")}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-5 md:grid-cols-2">
              <div>
                <Input
                  label={t("hotels.generalInfo.networkName")}
                  placeholder={t("hotels.form.enterWifiNetworkName")}
                  aria-invalid={Boolean(errors.wifiNetwork)}
                  {...register("wifiNetwork")}
                />
                <FieldError message={errors.wifiNetwork?.message} />
              </div>
              <div>
                <Input
                  type="password"
                  label={t("hotels.generalInfo.password")}
                  placeholder={t("hotels.form.enterWifiPassword")}
                  aria-invalid={Boolean(errors.wifiPassword)}
                  autoComplete="new-password"
                  {...register("wifiPassword")}
                />
                <FieldError message={errors.wifiPassword?.message} />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-foreground text-sm font-medium">
                  {t("hotels.form.wifiSecurity")}
                </label>
                <Select
                  value={watch("wifiSecurity")}
                  onValueChange={(value) =>
                    setValue("wifiSecurity", value as WifiSecurity, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue
                      placeholder={t("hotels.form.selectWifiSecurity")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {WIFI_SECURITY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {t(option.labelKey)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldError message={errors.wifiSecurity?.message} />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 bg-[#F3F4F6] shadow-none">
            <CardHeader>
              <CardTitle className="text-primary text-xl font-normal">
                {t("hotels.generalInfo.hotelMap")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <HotelMapPreview
                selectedFile={selectedMapFile}
                savedFileId={hotel.mapFileId}
                onUploadAnother={() => mapInputRef.current?.click()}
              />
              <label
                htmlFor="mapFileId"
                className="bg-background hover:bg-accent focus-within:ring-ring/50 relative flex min-h-36 cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed transition-colors focus-within:ring-3"
              >
                <Upload className="size-6" />
                <span className="text-muted-foreground text-sm">
                  {selectedMapFile?.name ??
                    (hotel.mapFileId
                      ? t("hotels.form.currentFileUploaded")
                      : t("hotels.generalInfo.uploadMap"))}
                </span>
                <input
                  id="mapFileId"
                  ref={mapInputRef}
                  type="file"
                  accept=".pdf"
                  className="sr-only"
                  onChange={handleMapFileChange}
                />
              </label>
            </CardContent>
          </Card>

          <Card className="border-0 bg-[#F3F4F6] shadow-none">
            <CardHeader>
              <CardTitle className="text-primary text-xl font-normal">
                {t("hotels.generalInfo.contacts")}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-5 lg:grid-cols-3">
              <Input
                label={t("hotels.generalInfo.assignedManager")}
                value={
                  assignedManager ? formatManagerLabel(assignedManager) : ""
                }
                readOnly
              />
              <Input
                label={t("hotels.generalInfo.phone")}
                value={assignedManager?.phone ?? ""}
                readOnly
              />
              <Input
                type="email"
                label={t("hotels.generalInfo.email")}
                value={assignedManager?.email ?? ""}
                readOnly
              />
            </CardContent>
          </Card>

          <Card className="border-0 bg-[#F3F4F6] shadow-none">
            <CardHeader>
              <CardTitle className="text-primary text-xl font-normal">
                {t("hotels.generalInfo.socialMedia")}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-5 lg:grid-cols-3">
              <div>
                <Input
                  type="url"
                  label={t("hotels.generalInfo.instagram")}
                  placeholder={t("hotels.generalInfo.instagramPlaceholder")}
                  aria-invalid={Boolean(errors.socialMediaInstagram)}
                  {...register("socialMediaInstagram")}
                />
                <FieldError message={errors.socialMediaInstagram?.message} />
              </div>
              <div>
                <Input
                  type="url"
                  label={t("hotels.generalInfo.facebook")}
                  placeholder={t("hotels.generalInfo.facebookPlaceholder")}
                  aria-invalid={Boolean(errors.socialMediaFacebook)}
                  {...register("socialMediaFacebook")}
                />
                <FieldError message={errors.socialMediaFacebook?.message} />
              </div>
              <div>
                <Input
                  type="url"
                  label={t("hotels.generalInfo.other")}
                  placeholder={t("hotels.generalInfo.otherSocialPlaceholder")}
                  aria-invalid={Boolean(errors.socialMediaOther)}
                  {...register("socialMediaOther")}
                />
                <FieldError message={errors.socialMediaOther?.message} />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 bg-[#F3F4F6] shadow-none">
            <CardHeader>
              <div className="flex items-center justify-between gap-4">
                <CardTitle className="text-primary text-xl font-normal">
                  {t("hotels.generalInfo.hotelPolicies")}
                </CardTitle>
                <Button
                  type="button"
                  variant="new_secondary"
                  size="lg"
                  onClick={() => setIsRuleDialogOpen(true)}
                >
                  <Plus />
                  {t("hotels.generalInfo.addRule")}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <PolicyRuleDialog
                open={isRuleDialogOpen}
                onOpenChange={setIsRuleDialogOpen}
                onSubmit={(rule) =>
                  setPolicyText((current) => appendPolicyRule(current, rule))
                }
              />
              <RichTextInput
                value={policyText}
                dataColorMode="light"
                onChange={setPolicyText}
                containerClassName="flex flex-col gap-1.5"
              />
            </CardContent>
          </Card>

          <div className="flex flex-col items-end justify-between gap-3 sm:flex-row sm:items-center">
            <p className="text-muted-foreground text-sm">
              {canSave
                ? t("hotels.generalInfo.unsavedChanges")
                : t("hotels.generalInfo.allChangesSaved")}
            </p>
            <Button
              type="submit"
              variant="new_default"
              size="lg"
              disabled={!canSave || isSaving}
            >
              <Save />
              {isSaving
                ? t("hotels.form.saving")
                : t("hotels.generalInfo.saveChanges")}
            </Button>
          </div>
        </form>
      </div>
    </main>
  );
};
